/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * UI Data Utilities
 * Pure functions for transforming and processing UI data from SSE responses.
 */

/**
 * Formatted UI data structure from TIP response
 */
export interface FormattedUiData {
  intro?: string;
  followUp?: string;
  properties?: FormattedUiProperty[];
  categoryList?: { properties?: FormattedUiProperty[] }[];
  content?: string;
  cta?: {
    text: string;
    id: string;
    fromDate?: string;
    toDate?: string;
    roomCount?: number;
    useRewardPoints?: boolean;
    userSpecifiedDate?: boolean;
  };
}

/**
 * Property data within formatted UI response
 */
export interface FormattedUiProperty {
  aiDescription?: string;
  propertyId?: string;
}

/**
 * Formats UI message from uiData structure.
 * Pure function - transforms uiData to a display string.
 *
 * @param uiData - The UI data from the TIP response (can be string, object, or null)
 * @param result - The full result object containing fallback response
 * @returns Formatted message string or null
 */
export function formatUiMessage(uiData: FormattedUiData | string | null | undefined, result: any): string | null {
  if (typeof uiData === 'string') {
    return uiData;
  }

  if (uiData && typeof uiData === 'object') {
    const intro = typeof uiData?.intro === 'string' ? uiData.intro.trim() : '';
    const followUp = typeof uiData?.followUp === 'string' ? uiData.followUp.trim() : '';
    const content = typeof uiData?.content === 'string' ? uiData.content.trim() : '';
    const properties = Array.isArray(
      uiData?.properties || uiData?.categoryList?.flatMap((cat: any) => cat?.properties ?? [])
    )
      ? uiData?.properties || uiData?.categoryList?.flatMap((cat: any) => cat?.properties ?? [])
      : [];

    const propertyLines = (properties as FormattedUiProperty[])
      .map((property: FormattedUiProperty, index: number) => {
        const description = typeof property?.aiDescription === 'string' ? property.aiDescription.trim() : '';
        return description ? `${index + 1}. ${description}` : '';
      })
      .filter(Boolean);

    const sections = [intro, propertyLines.length > 0 ? propertyLines.join('\n') : '', content, followUp].filter(
      Boolean
    );

    return sections.length > 0 ? sections.join('\n\n') : null;
  }

  return typeof result?.response === 'string' ? result.response : null;
}

/**
 * Marks best match properties in pending viewData based on uiData property IDs.
 * Mutates the pending.viewData edges in-place (intentional for performance).
 *
 * @param pending - The pending preview data containing viewData with edges
 * @param uiData - The UI data containing property IDs to mark as best match
 */
export function markBestMatchProperties(pending: any, uiData: FormattedUiData | string | null | undefined): void {
  const uiDataProperties =
    uiData && typeof uiData === 'object' && Array.isArray(uiData.properties)
      ? uiData.properties
      : uiData && typeof uiData === 'object' && Array.isArray(uiData?.categoryList)
      ? uiData.categoryList.flatMap((cat: any) => cat?.properties ?? [])
      : [];

  const uiDataPropertyIds = new Set(uiDataProperties.map((p: any) => p?.propertyId).filter(Boolean));

  if (
    Array.isArray(pending?.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges)
  ) {
    pending?.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges.forEach(
      (edge: any) => {
        const edgePropertyId = edge?.node?.property?.id;
        if (edgePropertyId && uiDataPropertyIds.has(edgePropertyId)) {
          edge.node.isBestMatch = true;
        }
      }
    );
  }
}
